<template>
  <section class="assessment-mode-selection" aria-labelledby="assessment-mode-title">
    <div class="assessment-mode-selection__header">
      <h1 id="assessment-mode-title" class="assessment-mode-selection__title">
        {{ $t('app.assessment_mode.selection.title') }}
      </h1>
      <h4 id="assessment-mode-subtitle" class="assessment-mode-selection__subtitle">
        {{ $t('app.assessment_mode.selection.subtitle') }}
      </h4>
    </div>

    <div class="assessment-mode-selection__grid">
      <AssessmentModeCard
          v-for="option in availableAssessmentModes"
          :key="option.id"
          :option="option"
          :locked="isLocked(option.id)"
          @select="handleModeSelected"
          @locked="handleModeSelected"
      />
    </div>

    <v-dialog
        v-model="showOfflineNotice"
        max-width="360"
        overlay-color="primaryblue"
        overlay-opacity="0.6"
    >
      <div id="text-mode-offline-notice" class="assessment-mode-offline-dialog">
        <span class="assessment-mode-offline-dialog__icon" aria-hidden="true">
          <v-icon>{{ offlineIcon }}</v-icon>
        </span>
        <p class="assessment-mode-offline-dialog__message">
          {{ $t(offlineNoticeToken) }}
        </p>
        <v-btn
            id="text-mode-offline-notice-ok"
            class="assessment-mode-offline-dialog__button"
            depressed
            @click="showOfflineNotice = false"
        >
          {{ $t('app.dialog.button.ok') }}
        </v-btn>
      </div>
    </v-dialog>
  </section>
</template>

<script lang="ts">
import router from '@/router';
import { computed, defineComponent, getCurrentInstance, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { mdiWifiOff } from '@mdi/js';
import AssessmentModeCard from './AssessmentModeCard.vue';
import { ASSESSMENT_MODE_ROUTE_LOCATIONS } from '../config/assessment-mode.routes';
import { modeRequiresConnection } from '../config/assessment-mode.connectivity';
import {
  AssessmentMode,
  AssessmentModeOption,
} from '../types/assessment-mode.types';
import { trackAssessmentModeSelected } from '../analytics/assessment-mode-analytics';
import { useCollectStore } from '@/stores/collect';
import { usePartnerConfigStore } from '@/stores/partnerConfig';

export default defineComponent({
  name: 'AssessmentModeSelection',

  components: {
    AssessmentModeCard,
  },

  setup() {
    const collectStore = useCollectStore();
    const partnerConfigStore = usePartnerConfigStore();
    const { availableAssessmentModes } = storeToRefs(partnerConfigStore);
    const proxy = getCurrentInstance()?.proxy as unknown as {
      $online?: boolean;
    } | undefined;

    const trackModeSelected = (
      mode: AssessmentMode,
      result: Parameters<typeof trackAssessmentModeSelected>[1],
    ): void => trackAssessmentModeSelected(mode, result, 'AssessmentModeSelection');

    const showOfflineNotice = ref(false);
    const offlineNoticeMode = ref<AssessmentMode>('text');
    const offlineNoticeToken = computed<string>(() => (offlineNoticeMode.value === 'guided'
      ? 'app.assessment_mode.offline.guided_unavailable'
      : 'app.assessment_mode.offline.text_unavailable'));
    const online = computed<boolean>(() => proxy?.$online !== false);

    const requiresConnection = (mode: AssessmentMode): boolean =>
      modeRequiresConnection(mode, partnerConfigStore.offline);

    const isLocked = (mode: AssessmentMode): boolean =>
      requiresConnection(mode) && !online.value;

    const showOfflineNoticeFor = (mode: AssessmentMode): void => {
      offlineNoticeMode.value = mode;
      showOfflineNotice.value = true;
    };

    const findOption = (mode: AssessmentMode): AssessmentModeOption | undefined => {
      return availableAssessmentModes.value.find((option: AssessmentModeOption) => option.id === mode);
    };

    const serverIsReachable = async (): Promise<boolean> => {
      try {
        return Boolean(await collectStore.checkIfServerIsAvailable());
      } catch {
        return false;
      }
    };

    const handleModeSelected = async (mode: AssessmentMode): Promise<void> => {
      const selectedOption = findOption(mode);

      if (!selectedOption || !selectedOption.enabled) {
        return;
      }

      if (isLocked(mode)) {
        trackModeSelected(mode, 'blocked_offline');
        showOfflineNoticeFor(mode);
        return;
      }

      if (requiresConnection(mode) && !await serverIsReachable()) {
        trackModeSelected(mode, 'blocked_unreachable');
        showOfflineNoticeFor(mode);
        return;
      }

      trackModeSelected(mode, 'opened');
      router.push(ASSESSMENT_MODE_ROUTE_LOCATIONS[mode]);
    };

    // if only one option exists, route before component load
    if (availableAssessmentModes.value.length === 1) {
      const onlyMode = availableAssessmentModes.value[0].id;

      if (isLocked(onlyMode)) {
        trackModeSelected(onlyMode, 'blocked_offline');
        showOfflineNoticeFor(onlyMode);
      } else {
        trackModeSelected(onlyMode, 'auto_selected');
        router.replace(ASSESSMENT_MODE_ROUTE_LOCATIONS[onlyMode]);
      }
    }

    return {
      availableAssessmentModes,
      handleModeSelected,
      isLocked,
      showOfflineNotice,
      offlineNoticeMode,
      offlineNoticeToken,
      offlineIcon: mdiWifiOff,
    };
  },
});
</script>
